/**
 * 地区库
 * 数据源 https://github.com/zengzhan/qqzeng-ip/blob/master/mysql/area.sql
 * 淘宝店铺  qqzeng-ip
 */
import { CDN_URL } from "@supplier/constants";

type AreaItem = {
    id: string;
    name: string;
    shortName?: string;
    childs?: AreaItem[];
};

type AreaOption = {
    label: string;
    value: unknown;
    children?: AreaOption[];
};

type AreaDisabledOption = {
    id: string;
    name: unknown;
    disabled: boolean;
    children?: AreaDisabledOption[];
};

class AreaLib {
    private areaData: AreaItem[];
    constructor() {
        this.areaData = [];
        this.loadFromCdn();
    }
    private loadFromCdn = async () => {
        const AREA_JSON_URL = `${CDN_URL.replace(/\/+$/, "")}/json/areas.json`;
        if (!AREA_JSON_URL) return;
        try {
            const res = await fetch(AREA_JSON_URL, { cache: "force-cache" });
            if (!res.ok) return;
            const data = (await res.json()) as AreaItem[];
            if (Array.isArray(data) && data.length) {
                this.areaData = data;
            }
        } catch {
            // Fallback to bundled data if CDN fetch fails.
        }
    };
    /** 获取子地区列表 */
    getAreaList = (provinceAdcode?: string, cityAdcode?: string) => {
        let area = this.areaData;
        if (provinceAdcode) {
            area = this.areaData.find(item => item.id === provinceAdcode)?.childs || [];
            if (cityAdcode) {
                area = area.find(item => item.id === cityAdcode)?.childs || [];
            }
        }
        return area;
    };
    /**根据adcode获取地区名 */
    getAreaName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.name;
    };
    /**获取完整省市区名 */
    getFullAreaName = (provinceAdcode?: string, cityAdcode?: string, regionAdcode?: string) => {
        let fullName = "";
        if (provinceAdcode) {
            fullName += this.getAreaName(provinceAdcode) ?? "";
            if (cityAdcode) {
                fullName += this.getAreaName(cityAdcode, { provinceAdcode }) ?? "";
                if (regionAdcode) {
                    fullName += this.getAreaName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "";
                }
            }
        }
        return fullName;
    };
    /**根据adcode获取简称 */
    getAreaShortName = (adcode?: string, options: { provinceAdcode?: string; cityAdcode?: string } = {}) => {
        const { provinceAdcode, cityAdcode } = options;
        const area = this.getAreaList(provinceAdcode, cityAdcode);
        return area.find(item => item.id === adcode)?.shortName;
    };
    /**获取完整省市简称 */
    getShortAreaName = (provinceAdcode: string, cityAdcode?: string, regionAdcode?: string, last: boolean = false) => {
        const provinceName = this.getAreaShortName(provinceAdcode) ?? "";
        const cityName = this.getAreaShortName(cityAdcode, { provinceAdcode }) ?? "";
        const regionName = this.getAreaShortName(regionAdcode, { provinceAdcode, cityAdcode }) ?? "";

        let fullName = provinceName;
        if (last) {
            fullName = regionName || cityName;
        } else {
            if (cityName) fullName += cityName;
            if (regionName) fullName += regionName;
        }
        // 定义直辖市名称列表
        const cities = ["北京", "上海", "天津", "重庆"];
        // 遍历每个直辖市名称并替换重复的部分
        cities.forEach(city => {
            const regex = new RegExp(`(${city})\\1`, "g"); // 正则表达式匹配重复的城市名称
            fullName = fullName.replace(regex, "$1"); // 将重复的城市名称替换为单个
        });
        return fullName;
    };
    /**areaLib列表转options列表 */
    areaList2Options = (areaList: AreaItem[]): AreaOption[] => {
        return areaList.map(item => {
            const option: AreaOption = {
                label: item.name,
                value: item.id
            };
            if (item.childs) {
                option.children = this.areaList2Options(item.childs);
            }
            return option;
        });
    };
    /**
     * areaLib 列表添加一个disable 属性
     * */
    areaListAddDisabled = (areaList: AreaItem[]): AreaDisabledOption[] => {
        return areaList.map(item => {
            const option: AreaDisabledOption = {
                ...item,
                disabled: false
            };
            if (item.childs) {
                option.children = this.areaListAddDisabled(item.childs);
            }
            return option;
        });
    };
    // 根据adcode获取城市区code
    getAreaCode = (adcode: string) => {
        const arr: string[] = [];
        if (
            // 省辖县、直筒子市 天地图（国标）不返回第三级名称 补齐标准地级市代码
            [
                "419001",
                "429004",
                "429005",
                "429006",
                "429021",
                "4419",
                "4420",
                "4604",
                "469001",
                "469002",
                "469005",
                "469006",
                "469007",
                "469021",
                "469022",
                "469023",
                "469024",
                "469025",
                "469026",
                "469027",
                "469028",
                "469029",
                "469030",
                "6202",
                "659001",
                "659002",
                "659003",
                "659004",
                "659005",
                "659006",
                "659007",
                "659008",
                "659009",
                "659010",
                "659011",
                "659012"
            ].includes(adcode)
        ) {
            arr.push(adcode.slice(0, 2), adcode, adcode);
            return arr;
        } else if (!["441900", "442000", "460400", "620200"].includes(adcode)) {
            arr.push(adcode.slice(0, 2), adcode.slice(0, 4), adcode);
            return arr;
        } else {
            arr.push(adcode.slice(0, 2), adcode.slice(0, 4));
            return arr;
        }
    };
}

export const areaLib = new AreaLib();
